// ---------------------------------------------------------------------------
// Canonical worldwide country list shared across apps.
//
// Built from `libphonenumber-js` (already a dependency) + `Intl.DisplayNames`,
// so it stays in sync with every ISO-3166 region the phone library supports —
// no separate hand-maintained list and no new dependency. This is the single
// source for both the phone-number country selector (InputField PhoneField)
// and the signup address country dropdown.
// ---------------------------------------------------------------------------

import { getCountries, getCountryCallingCode, type CountryCode } from 'libphonenumber-js';

export const DEFAULT_COUNTRY: CountryCode = 'JM';
export const PRIORITY_COUNTRIES: CountryCode[] = ['JM', 'GB', 'US', 'CA'];

export interface CountryOption {
  code: CountryCode;
  name: string;
  callingCode: string;
}

const buildCountryList = (): CountryOption[] => {
  let regionNames: Intl.DisplayNames | undefined;
  try {
    regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    regionNames = undefined;
  }

  const toOption = (code: CountryCode): CountryOption => ({
    code,
    name: regionNames?.of(code) || code,
    callingCode: getCountryCallingCode(code),
  });

  const all = getCountries();
  const priority = PRIORITY_COUNTRIES.filter((c) => all.includes(c)).map(toOption);
  const rest = all
    .filter((c) => !PRIORITY_COUNTRIES.includes(c))
    .map(toOption)
    .sort((a, b) => a.name.localeCompare(b.name));

  return [...priority, ...rest];
};

// Built lazily and memoized on first use so merely importing this module (or
// the @shared/components barrel) does no ICU work — only code that actually
// renders a phone/country selector pays the ~250-country build cost.
let _countryList: CountryOption[] | null = null;

/** Full worldwide list, priority countries pinned first, rest alpha by name. */
export const getCountryList = (): CountryOption[] => {
  if (!_countryList) _countryList = buildCountryList();
  return _countryList;
};

/**
 * Options for a plain country dropdown that stores the country display NAME
 * (e.g. 'United States'), matching the backend's free-form country string and
 * existing data. Priority countries pinned first.
 */
export const getCountryNameOptions = (): { value: string; label: string }[] =>
  getCountryList().map((c) => ({ value: c.name, label: c.name }));
